import React, { useState, useRef, useEffect } from "react";
import moment from 'moment';
import Swal from "sweetalert2";

import {
    Button, Input, Col, Badge, Card, CardBody, ListGroup, ListGroupItem, Collapse
} from "reactstrap";

import { useTranslation } from "react-i18next";
import * as commonFunc from 'scripts/common';
import '../../assets/css/chatfrag.css';


/* ─────────────────────────────────────────────
   Helper: render text that may contain HTML
   ───────────────────────────────────────────── */
const HtmlContent = ({ text }) => {
    if (!text) return null;
    if (/<[a-z][\s\S]*>/i.test(text)) {
        return <span dangerouslySetInnerHTML={{ __html: text }} />;
    }
    return <span>{text}</span>;
};


/* ─────────────────────────────────────────────
   KnowledgeSourceType enum mapping
   ───────────────────────────────────────────── */
const SOURCE_TYPE = {
    0: { label: "Doc", icon: "fa-file-alt", color: "primary" },
    1: { label: "Web", icon: "fa-globe", color: "info" },
    2: { label: "PDF", icon: "fa-file-pdf", color: "danger" },
    3: { label: "Chat", icon: "fa-comments", color: "warning" },
    4: { label: "Analyse", icon: "fa-chart-bar", color: "success" },
};

const getSourceType = (docId) => {
    const id = parseInt(docId, 10);
    return SOURCE_TYPE[id] || SOURCE_TYPE[0];
};


/* ─────────────────────────────────────────────
   Sub-component: renders a structured AI result
   { agent, tokens, summary, points, confidence, sources }
   ───────────────────────────────────────────── */
const StructuredResult = ({ content }) => {
    const [sourcesOpen, setSourcesOpen] = useState(false);

    let parsed = null;
    if (typeof content === "string") {
        try {
            parsed = JSON.parse(content);
        } catch {
            return <HtmlContent text={content} />;
        }
    } else if (typeof content === "object") {
        parsed = content;
    }

    if (!parsed || !parsed.summary) {
        return <HtmlContent text={typeof content === "string" ? content : JSON.stringify(content)} />;
    }

    const confidenceMap = {
        high: { color: "success", label: "Élevée" },
        medium: { color: "warning", label: "Moyenne" },
        low: { color: "danger", label: "Faible" },
    };
    const conf = confidenceMap[parsed.confidence] || { color: "secondary", label: parsed.confidence };

    return (
        <div className="cf-structured">
            {/* Summary */}
            <div className="cf-summary"><HtmlContent text={parsed.summary} /></div>

            {/* Points */}
            {parsed.points?.length > 0 && (
                <div className="cf-section">
                    <small className="cf-label text-muted">Points clés</small>
                    <ListGroup flush className="cf-points">
                        {parsed.points.map((pt, i) => (
                            <ListGroupItem key={i} className="cf-point-item">
                                <HtmlContent text={pt} />
                            </ListGroupItem>
                        ))}
                    </ListGroup>
                </div>
            )}

            {/* Confidence */}
            {parsed.confidence && (
                <div className="cf-meta-row">
                    <small className="text-muted">Confiance</small>
                    <Badge color={conf.color} pill>{conf.label}</Badge>
                </div>
            )}

            {/* Agent / Tokens */}
            {(parsed.agent || parsed.tokens) && (
                <div className="cf-meta-row">
                    {parsed.agent && (
                        <>
                            <small className="text-muted">Modèle</small>
                            <Badge color="info" pill>{parsed.agent}</Badge>
                        </>
                    )}
                    {parsed.tokens != null && (
                        <>
                            <small className="text-muted cf-ml">Tokens</small>
                            <Badge color="secondary" pill>{Math.round(parsed.tokens).toLocaleString()}</Badge>
                        </>
                    )}
                </div>
            )}

            {/* Sources — Collapsible */}
            {parsed.sources?.length > 0 && (
                <div className="cf-section">
                    <div
                        className="cf-sources-toggle"
                        onClick={() => setSourcesOpen(prev => !prev)}
                    >
                        <small className="cf-label text-muted">
                            Sources ({parsed.sources.length})
                        </small>
                        <i className={`fa fa-chevron-${sourcesOpen ? 'up' : 'down'} cf-toggle-icon`}></i>
                    </div>
                    <Collapse isOpen={sourcesOpen}>
                        <div className="cf-sources-list">
                            {parsed.sources.map((src, i) => (
                                <Card key={i} className="cf-source-card border-0">
                                    <CardBody className="cf-source-body">
                                        <div className="cf-source-header">
                                            <Badge color={getSourceType(src.docId).color} pill>
                                                <i className={`fa ${getSourceType(src.docId).icon} mr-1`}></i>
                                                {getSourceType(src.docId).label}
                                            </Badge>
                                            <small className="text-muted">Chunk #{src.chunkId}</small>
                                        </div>
                                        {src.quote && (
                                            <p className="cf-source-quote mb-0">
                                                « <HtmlContent text={src.quote} /> »
                                            </p>
                                        )}
                                    </CardBody>
                                </Card>
                            ))}
                        </div>
                    </Collapse>
                </div>
            )}
        </div>
    );
};


/* ─────────────────────────────────────────────
   ChatFrag (LiveChat fusionné)
   ───────────────────────────────────────────── */
const SENDER_ID = 2;
const RECEIVER_ID = 3;

export default function ChatFrag(props) {
    const { data, formManager, modelFctManager, bindPropResult, parentProps, isConfiguration, childShouldRerender, parentDisabled } = props;

    const { t } = useTranslation();
    const altT = (label, args) => { return (label && label.startsWith("i18n:")) ? t(label.replace("i18n:", ""), args) : label };
    const { register, getValues, setValue, errors, control } = formManager;

    const [results, setResults] = useState([]);
    const [messageInput, setMessageInput] = useState("");
    const [waiting, setWaiting] = useState(false);
    const messagesEndRef = useRef(null);

    // ── Helpers ──
    const getBindId = () => getValues(commonFunc.camelize(data?.argumentsObj?.bindIDTarget, true) || "none");
    const getSessionId = () => getValues(commonFunc.camelize(data?.argumentsObj?.sessionIDTarget, true) || "none");

    // ── Init: reload silently when session is available ──
    useEffect(() => {
        const sessionId = getSessionId();
        if (!commonFunc.isBlank(sessionId) && sessionId.toString() !== "-1") {
            reload(true);
        }
    }, []);

    // ── Auto-scroll ──
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [results]);

    // ── Load session ──
    const reload = (silent = false) => {
        if (!silent) {
            Swal.fire({ title: t("gal_processing"), didOpen: () => { Swal.showLoading(); } });
        }
        setWaiting(true);

        const bindId = getBindId();
        const sessionId = getSessionId();

        if (!commonFunc.isBlank(sessionId) && sessionId.toString() !== "-1") {
            commonFunc.postMethod("../api/LoadPromptFromSession", { bindId, sessionId }).then((response) => {
                setWaiting(false);
                Swal.close();
                if (response.data?.isSuccess) {
                    setResults([]);
                }
            });
        } else {
            setWaiting(false);
            if (!silent) {
                Swal.fire({ icon: "error", title: t("gal_fail"), html: "Aucune session associée", didOpen: () => { Swal.hideLoading(); } });
            }
        }
    };

    // ── Send message ──
    const sendMessage = async () => {
        try {
            const text = messageInput.trim();
            if (!text) return;

            setMessageInput("");
            setWaiting(true);

            // Show user message immediately
            setResults(prev => [...prev, {
                tenantUserAccess: { idTenantUser: SENDER_ID, name: "Utilisateur" },
                dSysMod: new Date(),
                comments: text,
                isStructured: false
            }]);

            const bindId = getBindId();
            const sessionId = getSessionId();

            if (!commonFunc.isBlank(sessionId)) {
                const message = {
                    text,
                    sender: { id: SENDER_ID, name: "Utilisateur" },
                    receiver: { id: RECEIVER_ID, name: "IA" }
                };
                const req = { bindId, sessionId, message, waitForCompletion: true, ModelLLM: "gpt-51" }; //"gpt-5-mini" a mettre ds le modele!

                commonFunc.postMethod("../api/lead/PostLeadMessage", req).then((response) => {
                    Swal.close();
                    const respData = response.data;
                    if (respData?.isSuccess) {
                        setResults(prev => [...prev, {
                            tenantUserAccess: { idTenantUser: RECEIVER_ID, name: "IA" },
                            dSysMod: new Date(),
                            comments: respData.result,
                            isStructured: true
                        }]);
                        setWaiting(false);
                    } else {
                        setWaiting(false);
                        Swal.fire({ icon: "error", title: t("gal_fail"), html: t("gal_error") + "<br/>" + respData.errorMessage });
                    }
                });
            } else {
                setWaiting(false);
                Swal.fire({ icon: "error", title: t("gal_fail"), html: "Aucune session associée" });
            }
        } catch (error) {
            setWaiting(false);
            alert(error);
        }
    };

    // ── Render ──
    return (
        <Col data-name="ChatFrag" data-element={data.objType} xs="12">
            <div className="cf-root">
                <div className="cf-chat">
                    {/* Header */}
                    <div className="cf-header">
                        <h2 className="mb-0">Chat IA</h2>
                        <Button color="light" size="sm" className="d-none btn-icon rounded-circle" onClick={reload} title="Recharger">
                            <i className="fa fa-sync"></i>
                        </Button>
                    </div>

                    {/* Messages */}
                    <div className="cf-messages">
                        {results
                            .sort((a, b) => new Date(a.dSysMod) - new Date(b.dSysMod))
                            .map((result, idx) => {
                                const isSender = result.tenantUserAccess.idTenantUser === SENDER_ID;
                                return (
                                    <div key={idx} className={isSender ? "cf-msg cf-msg--sent" : "cf-msg cf-msg--received"}>
                                        <p className="cf-msg-name text-muted">{result.tenantUserAccess.name}</p>
                                        <div className="cf-msg-bubble">
                                            {result.isStructured
                                                ? <StructuredResult content={result.comments} />
                                                : <span>{result.comments}</span>}
                                        </div>
                                        <p className="cf-msg-time text-muted">
                                            {moment(result.dSysMod).format("DD/MM/YYYY HH:mm")}
                                        </p>
                                    </div>
                                );
                            })}

                        {/* Typing indicator */}
                        {waiting && (
                            <div className="cf-msg cf-msg--received">
                                <small className="cf-msg-name text-muted">IA</small>
                                <div className="cf-msg-bubble cf-typing">
                                    <span className="cf-dot"></span>
                                    <span className="cf-dot"></span>
                                    <span className="cf-dot"></span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="cf-input">
                        <Input
                            type="text"
                            value={messageInput}
                            onChange={(e) => setMessageInput(e.target.value)}
                            placeholder="Votre requête"
                            disabled={waiting}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !waiting && messageInput.trim()) {
                                    sendMessage();
                                    e.preventDefault();
                                }
                            }}
                        />
                        {!waiting ? (
                            <Button color="primary" className="btn-icon rounded-circle" onClick={sendMessage} disabled={!messageInput.trim()}>
                                <i className="fa fa-paper-plane"></i>
                            </Button>
                        ) : (
                            <i className="fa fa-sync fa-spin fa-xl cf-spinner"></i>
                        )}
                    </div>
                </div>
            </div>
        </Col>
    );
}
